// S4 (v0.15.0) — Tenant SSO (SAML 2.0) configuration.
import { useEffect, useState } from "react";
import { CopyIcon } from "lucide-react";

import {
  useDeleteSSOConfig,
  useRoles,
  useSSOConfig,
  useUpsertSSOConfig,
} from "@/api/s4";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useTenantStore } from "@/store/tenant";

export default function SSOConfigPage() {
  const { data: cfg } = useSSOConfig();
  const { data: roles = [] } = useRoles();
  const upsert = useUpsertSSOConfig();
  const del = useDeleteSSOConfig();

  const [provider, setProvider] = useState("");
  const [xml, setXml] = useState("");
  const [emailAttr, setEmailAttr] = useState(
    "http://schemas.xmlsoap.org/ws/2005/05/identity/claims/emailaddress",
  );
  const [nameAttr, setNameAttr] = useState(
    "http://schemas.xmlsoap.org/ws/2005/05/identity/claims/name",
  );
  const [defaultRoleId, setDefaultRoleId] = useState("");
  const [jit, setJit] = useState(true);
  const [enabled, setEnabled] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  const tenantName = useTenantStore((s) => s.currentTenantName);
  const tenantSlug = (cfg?.sp_entity_id ?? "").replace(/^sview360_/, "");
  const acsUrl =
    tenantSlug && typeof window !== "undefined"
      ? `${window.location.origin}/api/v1/sso/${tenantSlug}/callback`
      : "";

  useEffect(() => {
    if (cfg) {
      setProvider(cfg.provider_name);
      setEmailAttr(cfg.attribute_mapping?.email || emailAttr);
      setNameAttr(cfg.attribute_mapping?.name || nameAttr);
      setDefaultRoleId(cfg.default_role_id ?? "");
      setJit(cfg.allow_jit_provisioning);
      setEnabled(cfg.is_enabled);
      // Don't pre-fill xml — we don't want to round-trip the whole
      // metadata document each save. Admin re-pastes only if changing.
    }
  }, [cfg]);

  async function handleSave() {
    setError(null);
    setSaved(false);
    if (!xml.trim() && !cfg) {
      setError("Paste your IdP metadata XML to create the configuration.");
      return;
    }
    try {
      await upsert.mutateAsync({
        provider_name: provider,
        idp_metadata_xml: xml.trim() || cfg?.idp_entity_id || "",
        attribute_mapping: { email: emailAttr, name: nameAttr },
        default_role_id: defaultRoleId || null,
        allow_jit_provisioning: jit,
        is_enabled: enabled,
      });
      setSaved(true);
    } catch (err) {
      const detail = (err as { response?: { data?: { detail?: string } } })
        .response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Save failed");
    }
  }

  async function handleDelete() {
    if (!confirm("Remove SSO configuration? Users will no longer be able to sign in via SSO."))
      return;
    await del.mutateAsync();
    setEnabled(false);
    setProvider("");
    setXml("");
  }

  function copy(value: string) {
    navigator.clipboard.writeText(value);
  }

  return (
    <div className="mx-auto max-w-3xl space-y-4">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">
          SSO Configuration
        </h1>
        <p className="text-sm text-muted-foreground">
          {tenantName ? `${tenantName} — ` : ""}SAML 2.0
        </p>
      </div>

      <Card>
        <CardContent className="space-y-4 p-4">
          <div>
            <Label>Provider Name</Label>
            <Input
              value={provider}
              onChange={(e) => setProvider(e.target.value)}
              placeholder="Azure AD, Okta, Google, …"
            />
          </div>
          <div>
            <Label>IdP Metadata XML</Label>
            <textarea
              className="h-48 w-full rounded border border-border bg-background p-2 font-mono text-xs"
              value={xml}
              onChange={(e) => setXml(e.target.value)}
              placeholder={
                cfg
                  ? "Paste new XML only if rotating the IdP metadata"
                  : "Paste your IdP's SAML metadata XML here"
              }
            />
            <p className="mt-1 text-xs text-muted-foreground">
              Download this from your IdP's SAML application settings.
            </p>
          </div>
          <div className="grid gap-3 md:grid-cols-2">
            <div>
              <Label>Email Attribute</Label>
              <Input
                value={emailAttr}
                onChange={(e) => setEmailAttr(e.target.value)}
              />
            </div>
            <div>
              <Label>Name Attribute</Label>
              <Input
                value={nameAttr}
                onChange={(e) => setNameAttr(e.target.value)}
              />
            </div>
          </div>
          <div>
            <Label>Default Role for new SSO users</Label>
            <select
              value={defaultRoleId}
              onChange={(e) => setDefaultRoleId(e.target.value)}
              className="w-full rounded border border-border bg-background px-2 py-1 text-sm"
            >
              <option value="">— No default role —</option>
              {roles.map((r) => (
                <option key={r.id} value={r.id}>
                  {r.name}
                </option>
              ))}
            </select>
          </div>
          <div className="space-y-2">
            <label className="inline-flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={jit}
                onChange={(e) => setJit(e.target.checked)}
              />
              Allow JIT provisioning — automatically create accounts
              on first SSO login.
            </label>
            <label className="inline-flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={enabled}
                onChange={(e) => setEnabled(e.target.checked)}
              />
              Enable SSO for this tenant.
            </label>
          </div>
          {error && (
            <p className="rounded border border-red-500/40 bg-red-500/10 px-3 py-2 text-sm text-red-600">
              {error}
            </p>
          )}
          {saved && (
            <p className="rounded border border-emerald-500/40 bg-emerald-500/10 px-3 py-2 text-sm text-emerald-700">
              Saved.
            </p>
          )}
          <div className="flex justify-end gap-2">
            {cfg && (
              <Button variant="destructive" onClick={handleDelete}>
                Delete Config
              </Button>
            )}
            <Button onClick={handleSave} disabled={upsert.isPending}>
              {upsert.isPending ? "Saving…" : "Save Configuration"}
            </Button>
          </div>
        </CardContent>
      </Card>

      {cfg && (
        <Card>
          <CardContent className="space-y-3 p-4">
            <p className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
              Configure these values in your Identity Provider
            </p>
            <SPField label="SP Entity ID" value={cfg.sp_entity_id} onCopy={copy} />
            <SPField label="ACS URL" value={acsUrl} onCopy={copy} />
            <SPField
              label="IdP Entity ID (parsed)"
              value={cfg.idp_entity_id}
              onCopy={copy}
            />
            <SPField
              label="IdP SSO URL (parsed)"
              value={cfg.idp_sso_url}
              onCopy={copy}
            />
          </CardContent>
        </Card>
      )}
    </div>
  );
}

function SPField({
  label,
  value,
  onCopy,
}: {
  label: string;
  value: string;
  onCopy: (v: string) => void;
}) {
  return (
    <div>
      <p className="text-xs text-muted-foreground">{label}</p>
      <div className="flex items-center gap-2">
        <code className="flex-1 break-all rounded bg-muted/40 px-2 py-1 font-mono text-xs">
          {value || "—"}
        </code>
        {value && (
          <Button size="sm" variant="ghost" onClick={() => onCopy(value)}>
            <CopyIcon className="h-3 w-3" />
          </Button>
        )}
      </div>
    </div>
  );
}
